// Recipe Module Phase 1 (docs/RECIPE_MODULE.md) — create/edit a percentage-based recipe:
// ingredient lines expressed as a % of the FINAL BATCH weight (a real bill of materials — every
// line, including the cooked base ingredient's own line, sums to ~100%), an optional "base
// ingredient" (the real raw material cooked down into one of those lines, e.g. raw mushrooms into
// the "cooked mushroom" line — links that line to its own real purchase cost instead of a normal
// ingredient price), plain ordered process steps (auto-translated to Spanish on save), and a live
// cost-per-kg preview (mirrors AdminProductPage.js's recipe cost badge, reusing the same
// ingredientService.getCurrentPrices() price map).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import recipeService from '../services/recipeService';
import ingredientService from '../services/ingredientService';
import SearchableSelect from '../components/SearchableSelect';
import GenerateRecipeModal from '../components/GenerateRecipeModal';

export default function PercentageRecipeEditorPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const location = useLocation();

  const [ingredients, setIngredients] = useState([]);
  const [priceMap, setPriceMap] = useState({});

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [yieldBasisLabel, setYieldBasisLabel] = useState('');
  const [baseIngredientId, setBaseIngredientId] = useState('');
  const [expectedYieldPercent, setExpectedYieldPercent] = useState('');
  const [outputIngredientId, setOutputIngredientId] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [lines, setLines] = useState([{ ingredientId: '', percentage: '', note: '' }]);
  const [steps, setSteps] = useState([{ instruction: '' }]);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    ingredientService.getIngredients().then(setIngredients).catch(() => setIngredients([]));
    ingredientService.getCurrentPrices()
      .then(rows => {
        const map = {};
        for (const r of rows) map[String(r.ingredientId)] = Number(r.pricePerKg);
        setPriceMap(map);
      })
      .catch(() => {});
  }, []);

  const loadRecipe = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError('');
    try {
      const data = await recipeService.getPercentageRecipe(id);
      setName(data.name || '');
      setCategory(data.category || '');
      setYieldBasisLabel(data.yieldBasisLabel || '');
      setBaseIngredientId(data.baseIngredientId ? String(data.baseIngredientId) : '');
      setExpectedYieldPercent(data.expectedYieldPercent != null ? String(data.expectedYieldPercent) : '');
      setOutputIngredientId(data.outputIngredientId ? String(data.outputIngredientId) : '');
      setNotes(data.notes || '');
      setStatus(data.status || 'draft');
      setLines((data.lines || []).map(l => ({ ingredientId: String(l.ingredientId), percentage: String(l.percentage), note: l.note || '' })));
      setSteps((data.steps || []).length > 0 ? data.steps.map(s => ({ instruction: s.instruction })) : [{ instruction: '' }]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load recipe.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { loadRecipe(); }, [loadRecipe]);

  const ingredientOptions = useMemo(() => ingredients.map(i => ({
    value: String(i.id),
    label: i.rawMaterialCode ? `${i.name} (${i.rawMaterialCode})` : i.name,
  })), [ingredients]);

  // Normalize+substring fuzzy match against the real catalog — the AI returns plain ingredient
  // names (it doesn't know our ids), same fallback-matching convention already used elsewhere in
  // this app (receipt scan, PO scan) when an AI guess needs reconciling against real data.
  const fuzzyMatchIngredient = (name) => {
    if (!name) return null;
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = norm(name);
    const exact = ingredients.find(i => norm(i.name) === target);
    if (exact) return exact;
    return ingredients.find(i => norm(i.name).includes(target) || target.includes(norm(i.name))) || null;
  };

  const applyDraft = (draft) => {
    setName(draft.name || '');
    setCategory(draft.category || '');
    setYieldBasisLabel(draft.yieldBasisLabel || '');
    setLines((draft.lines || []).map(l => {
      const match = fuzzyMatchIngredient(l.ingredientName);
      return {
        ingredientId: match ? String(match.id) : '',
        percentage: String(l.percentage ?? ''),
        note: match ? (l.note || '') : `AI suggested: ${l.ingredientName}${l.note ? ` — ${l.note}` : ''}`,
      };
    }));
    setSteps((draft.steps || []).map(s => ({ instruction: s })));
    setShowGenerateModal(false);
  };

  // A draft handed off from the Knowledge Base chat's "➕ Add to your recipes" button arrives via
  // router state rather than the in-page modal — apply it once the real ingredient catalog is
  // loaded (so fuzzy-matching has something to match against), then clear the state so it doesn't
  // re-apply on a back-navigation or refresh.
  useEffect(() => {
    if (isNew && ingredients.length > 0 && location.state?.generatedDraft) {
      applyDraft(location.state.generatedDraft);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, ingredients, location.state]);

  const baseIngredientName = useMemo(() => {
    const ing = ingredients.find(i => String(i.id) === String(baseIngredientId));
    return ing ? ing.name : '';
  }, [ingredients, baseIngredientId]);

  // When a base ingredient is picked, it MUST also exist as a line (its own % of the final
  // batch) — auto-insert a blank-percentage line for it so the user can't forget, rather than
  // relying on them to remember to add it themselves.
  useEffect(() => {
    if (!baseIngredientId) return;
    setLines(prev => {
      if (prev.some(l => l.ingredientId === baseIngredientId)) return prev;
      const firstEmpty = prev.findIndex(l => !l.ingredientId);
      const newLine = { ingredientId: baseIngredientId, percentage: '', note: '' };
      if (firstEmpty !== -1) {
        return prev.map((l, i) => (i === firstEmpty ? newLine : l));
      }
      return [newLine, ...prev];
    });
  }, [baseIngredientId]);

  const addLine = () => setLines(prev => [...prev, { ingredientId: '', percentage: '', note: '' }]);
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));
  const setLineField = (idx, field, value) => setLines(prev => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));

  const addStep = () => setSteps(prev => [...prev, { instruction: '' }]);
  const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));
  const setStepField = (idx, value) => setSteps(prev => prev.map((s, i) => (i === idx ? { instruction: value } : s)));

  // Mirrors the backend's computeCostEstimate() exactly — see routes/recipeRoutes.js. The base
  // ingredient's own line is priced from its raw cost (amortized through Typical Yield %), not
  // from a normal ingredient price lookup; every other line is priced normally.
  const costPreview = useMemo(() => {
    const baseLine = baseIngredientId ? lines.find(l => l.ingredientId === baseIngredientId) : null;
    let otherLinesCost = 0;
    let unpriced = 0;
    let validLines = 0;
    for (const l of lines) {
      const pct = parseFloat(l.percentage);
      if (!l.ingredientId || !pct || isNaN(pct)) continue;
      validLines++;
      if (l === baseLine) continue; // priced separately below
      const price = priceMap[l.ingredientId];
      if (price == null) { unpriced++; continue; }
      otherLinesCost += (pct / 100) * price;
    }

    let baseCostPerKg = null;
    if (baseIngredientId) {
      const basePrice = priceMap[baseIngredientId];
      const yieldPct = parseFloat(expectedYieldPercent);
      const baseLinePct = baseLine ? parseFloat(baseLine.percentage) : NaN;
      if (basePrice != null && yieldPct > 0 && baseLinePct > 0) {
        baseCostPerKg = (baseLinePct / yieldPct) * basePrice;
      }
    }

    const missingBaseCost = baseIngredientId && baseCostPerKg == null;
    const totalCost = validLines === 0 || unpriced > 0 || missingBaseCost ? null : otherLinesCost + (baseCostPerKg || 0);
    return { unpriced, validLines, baseCostPerKg, totalCost };
  }, [lines, priceMap, baseIngredientId, expectedYieldPercent]);

  const totalPercentage = useMemo(() => lines.reduce((s, l) => s + (parseFloat(l.percentage) || 0), 0), [lines]);

  const handleSave = async () => {
    setError('');
    if (!name.trim()) return setError('Name is required.');
    const validLines = lines.filter(l => l.ingredientId && l.percentage !== '');
    if (validLines.length === 0) return setError('At least one ingredient line is required.');
    for (const l of validLines) {
      if (isNaN(parseFloat(l.percentage)) || parseFloat(l.percentage) <= 0) {
        return setError('Every ingredient line needs a positive percentage.');
      }
    }
    if (baseIngredientId && !validLines.some(l => l.ingredientId === baseIngredientId)) {
      return setError(`Add ${baseIngredientName || 'the base ingredient'} as an ingredient line too, with its own % of the final batch (e.g. 73% cooked) — otherwise the rest of the recipe can't add up to 100%.`);
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category: category.trim() || null,
        yieldBasisLabel: yieldBasisLabel.trim() || (baseIngredientName ? `Cooked ${baseIngredientName} weight (kg)` : null),
        baseIngredientId: baseIngredientId ? Number(baseIngredientId) : null,
        expectedYieldPercent: expectedYieldPercent !== '' ? Number(expectedYieldPercent) : null,
        outputIngredientId: outputIngredientId ? Number(outputIngredientId) : null,
        notes: notes.trim() || null,
        lines: validLines.map(l => ({ ingredientId: Number(l.ingredientId), percentage: Number(l.percentage), note: l.note.trim() || null })),
        steps: steps.filter(s => s.instruction.trim()).map(s => ({ instruction: s.instruction.trim() })),
      };
      if (isNew) {
        const created = await recipeService.createPercentageRecipe(payload);
        navigate(`/recipes/percentage/${created.id}/edit`);
      } else {
        await recipeService.updatePercentageRecipe(id, payload);
        await loadRecipe();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save recipe.');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await recipeService.approvePercentageRecipe(id);
      await loadRecipe();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve recipe.');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center text-muted py-5">
        <div className="spinner-border spinner-border-sm me-2" />Loading…
      </div>
    );
  }

  const CARD_STYLE = (color) => ({ border: '1px solid #e9ecef', borderTopWidth: 3, borderTopStyle: 'solid', borderTopColor: color });
  const SECTION_HEADER_STYLE = { fontSize: 11, letterSpacing: 1, fontWeight: 700 };

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center mb-4 pb-3 gap-2" style={{ borderBottom: '2px solid #f1f3f5' }}>
        <div>
          <h4 className="mb-1">{isNew ? '✨ New Recipe' : name}</h4>
          {!isNew && (
            <span className={`badge rounded-pill ${status === 'approved' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: 12, padding: '5px 12px' }}>
              {status === 'approved' ? '✓ Approved' : 'Draft'}
            </span>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {isNew && (
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowGenerateModal(true)}>✨ Generate with AI</button>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/recipes')}>← Back to Library</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {showGenerateModal && (
        <GenerateRecipeModal recipeType="percentage" onApply={applyDraft} onClose={() => setShowGenerateModal(false)} />
      )}

      <div className="card mb-3" style={CARD_STYLE('#0d6efd')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-3" style={SECTION_HEADER_STYLE}>🏷️ Recipe Details</div>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Name <span className="text-danger">*</span></label>
              <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Truffle Sauce" />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Category</label>
              <input className="form-control" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Sauces" />
            </div>
          </div>
          <div className="mb-1">
            <label className="form-label small fw-semibold">Notes</label>
            <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#6f42c1')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-1" style={SECTION_HEADER_STYLE}>🧪 Base Ingredient (optional)</div>
          <div className="text-muted mb-3" style={{ fontSize: 12 }}>
            If this recipe starts by cooking down a raw ingredient (e.g. raw mushrooms into a
            sauce), link it here — it's automatically added below as an ingredient line too, since
            it's still part of the final batch (e.g. 73% cooked mushroom). Its real purchase cost
            comes from the raw weight entered when the recipe is run, not from a normal ingredient
            price — and running it will ask for both the raw weight and the cooked/yield weight so
            the real yield loss is calculated instead of guessed.
          </div>
          <div className="row g-3">
            <div className="col-md-5">
              <label className="form-label small fw-semibold">Raw Ingredient</label>
              <SearchableSelect
                options={ingredientOptions}
                value={baseIngredientId}
                onChange={setBaseIngredientId}
                placeholder="None — this recipe has no raw cook-down step"
                size="sm"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-semibold">Typical Yield %</label>
              <div className="input-group input-group-sm">
                <input type="number" step="0.01" className="form-control" value={expectedYieldPercent}
                  onChange={e => setExpectedYieldPercent(e.target.value)} disabled={!baseIngredientId}
                  placeholder="e.g. 68" />
                <span className="input-group-text">%</span>
              </div>
              <div className="form-text" style={{ fontSize: 11 }}>% of raw weight retained after cooking — estimate only, for the cost preview below.</div>
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Yield Basis Label</label>
              <input className="form-control form-control-sm" value={yieldBasisLabel} onChange={e => setYieldBasisLabel(e.target.value)}
                placeholder={baseIngredientName ? `Cooked ${baseIngredientName} weight (kg)` : 'e.g. Cooked mushroom weight (kg)'} />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#0dcaf0')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-1" style={SECTION_HEADER_STYLE}>🏷️ Traceability (optional)</div>
          <div className="text-muted mb-3" style={{ fontSize: 12 }}>
            Link this recipe's finished batch to a real ingredient (e.g. a "Truffle Sauce"
            ingredient) and running it will auto-assign a lot code and credit the real output into
            Ingredient Inventory — so when this sauce is later used in another recipe/product, its
            lot code resolves to a real, traceable record instead of being a dead end.
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Output Ingredient</label>
              <SearchableSelect
                options={ingredientOptions}
                value={outputIngredientId}
                onChange={setOutputIngredientId}
                placeholder="None — this run won't be credited to inventory"
                size="sm"
              />
              <div className="form-text" style={{ fontSize: 11 }}>Doesn't exist yet? Create it first in Ingredients Hub, then link it here.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#198754')}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="text-uppercase text-muted" style={SECTION_HEADER_STYLE}>
              🥘 Ingredients (% of final batch)
            </div>
            <div className="text-end">
              <div className="small" style={{ color: Math.abs(totalPercentage - 100) > 1 ? '#fd7e14' : '#6c757d' }}>
                Lines total: {totalPercentage.toFixed(2)}%{Math.abs(totalPercentage - 100) > 1 && ' ⚠ should be ~100%'}
              </div>
              <div className="fw-semibold" style={{ fontSize: 18, color: '#198754' }}>
                {costPreview.totalCost == null ? '—' : `$${costPreview.totalCost.toFixed(4)}/kg`}
                {costPreview.unpriced > 0 && (
                  <span className="text-warning ms-1" style={{ fontSize: 11 }}>
                    ({costPreview.unpriced} unpriced)
                  </span>
                )}
              </div>
              {costPreview.baseCostPerKg != null && (
                <div className="text-muted" style={{ fontSize: 11 }}>
                  incl. ${costPreview.baseCostPerKg.toFixed(4)}/kg raw {baseIngredientName}
                </div>
              )}
            </div>
          </div>
          <div className="table-responsive">
          <table className="table table-sm">
            <thead className="table-light">
              <tr>
                <th style={{ width: '45%' }}>Ingredient</th>
                <th style={{ width: 120 }}>%</th>
                <th>Note</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const isBase = !!baseIngredientId && l.ingredientId === baseIngredientId;
                return (
                  <tr key={idx} className={isBase ? 'table-light' : ''}>
                    <td className="align-top pt-2">
                      <SearchableSelect
                        options={ingredientOptions}
                        value={l.ingredientId}
                        onChange={v => setLineField(idx, 'ingredientId', v)}
                        placeholder="Select ingredient…"
                        size="sm"
                      />
                      {isBase && <span className="badge bg-light text-dark border mt-1" style={{ fontSize: 10 }}>🧪 base — cooked weight</span>}
                    </td>
                    <td className="align-top pt-2">
                      <div className="input-group input-group-sm">
                        <input type="number" step="0.01" className="form-control" value={l.percentage}
                          onChange={e => setLineField(idx, 'percentage', e.target.value)} />
                        <span className="input-group-text">%</span>
                      </div>
                    </td>
                    <td className="align-top pt-2">
                      <input type="text" className="form-control form-control-sm" value={l.note}
                        onChange={e => setLineField(idx, 'note', e.target.value)} placeholder="optional" />
                    </td>
                    <td className="align-top pt-2">
                      <button className="btn btn-sm btn-outline-danger" onClick={() => removeLine(idx)} disabled={lines.length <= 1}>
                        &times;
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <button className="btn btn-sm btn-outline-primary" onClick={addLine}>+ Add Ingredient</button>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#fd7e14')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-1" style={SECTION_HEADER_STYLE}>📋 Process Steps</div>
          <div className="text-muted mb-3" style={{ fontSize: 12 }}>
            Plain checklist staff work through when running this recipe — no required temperature/
            timer logging. Automatically translated to Spanish when saved, so staff with their
            account language set to Spanish can read and follow them too.
          </div>
          {steps.map((s, idx) => (
            <div key={idx} className="d-flex gap-2 mb-2 align-items-start">
              <span className="text-muted pt-2" style={{ width: 24 }}>{idx + 1}.</span>
              <textarea className="form-control form-control-sm" rows={1} value={s.instruction}
                onChange={e => setStepField(idx, e.target.value)} placeholder="e.g. Cook mushrooms until fully reduced" />
              <button className="btn btn-sm btn-outline-danger" onClick={() => removeStep(idx)} disabled={steps.length <= 1}>
                &times;
              </button>
            </div>
          ))}
          <button className="btn btn-sm btn-outline-primary" onClick={addStep}>+ Add Step</button>
        </div>
      </div>

      <div className="d-flex gap-2">
        <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? '✨ Create Recipe' : '💾 Save Changes'}
        </button>
        {!isNew && status === 'draft' && (
          <button className="btn btn-success px-4" onClick={handleApprove} disabled={approving}>
            {approving ? 'Approving…' : '✓ Approve Recipe'}
          </button>
        )}
      </div>
    </div>
  );
}
